import React from 'react';
import cx from 'classnames';

import styles from './Icon.module.scss';
import usePresenter from './Icon.presenter';
import { IconCombinedProps } from './types';

import { ReactComponent as AddIcon } from '../../../resources/icons/Add.svg';
import { ReactComponent as AddFilledIcon } from '../../../resources/icons/AddFilled.svg';
import { ReactComponent as AddOutlineIcon } from '../../../resources/icons/AddOutline.svg';
import { ReactComponent as AddRoundedFillIcon } from '../../../resources/icons/AddRoundedFill.svg';
import { ReactComponent as ArrowDownIcon } from '../../../resources/icons/ArrowDown.svg';
import { ReactComponent as ArrowRightIcon } from '../../../resources/icons/ArrowRight.svg';
import { ReactComponent as ArrowUpIcon } from '../../../resources/icons/ArrowUp.svg';
import { ReactComponent as AttachmentIcon } from '../../../resources/icons/Attachment.svg';
import { ReactComponent as BackIcon } from '../../../resources/icons/Back.svg';
import { ReactComponent as CalendarIcon } from '../../../resources/icons/Calendar.svg';
import { ReactComponent as CalendarFilledIcon } from '../../../resources/icons/CalendarFilled.svg';
import { ReactComponent as CardMenuIcon } from '../../../resources/icons/CardMenu.svg';
import { ReactComponent as CheckBoxFilledIcon } from '../../../resources/icons/CheckBoxFilled.svg';
import { ReactComponent as CheckIcon } from '../../../resources/icons/Check.svg';
import { ReactComponent as CheckboxIcon } from '../../../resources/icons/Checkbox.svg';
import { ReactComponent as ChevronDownIcon } from '../../../resources/icons/ChevronDown.svg';
import { ReactComponent as ChevronLeftIcon } from '../../../resources/icons/ChevronLeft.svg';
import { ReactComponent as ChevronRightIcon } from '../../../resources/icons/ChevronRight.svg';
import { ReactComponent as ChevronUpIcon } from '../../../resources/icons/ChevronUp.svg';
import { ReactComponent as ClockIcon } from '../../../resources/icons/Clock.svg';
import { ReactComponent as CloseIcon } from '../../../resources/icons/Close.svg';
import { ReactComponent as CodeIcon } from '../../../resources/icons/Code.svg';
import { ReactComponent as CodeViewIcon } from '../../../resources/icons/CodeView.svg';
import { ReactComponent as CopyIcon } from '../../../resources/icons/Copy.svg';
import { ReactComponent as DataFilledIcon } from '../../../resources/icons/DataFilled.svg';
import { ReactComponent as DataOutlineIcon } from '../../../resources/icons/DataOutline.svg';
import { ReactComponent as DatePickerFilledIcon } from '../../../resources/icons/DatePickerFilled.svg';
import { ReactComponent as DatePickerOutlineIcon } from '../../../resources/icons/DatePickerOutline.svg';
import { ReactComponent as DeleteIcon } from '../../../resources/icons/Delete.svg';
import { ReactComponent as DeleteUserIcon } from '../../../resources/icons/DeleteUser.svg';
import { ReactComponent as DocumentCircleIcon } from '../../../resources/icons/DocumentCircle.svg';
import { ReactComponent as DocumentIcon } from '../../../resources/icons/Document.svg';
import { ReactComponent as DocumentFillIcon } from '../../../resources/icons/DocumentFill.svg';
import { ReactComponent as DropDownFilledIcon } from '../../../resources/icons/DropDownFilled.svg';
import { ReactComponent as DropDownOutlineIcon } from '../../../resources/icons/DropDownOutline.svg';
import { ReactComponent as Email } from '../../../resources/icons/Email.svg';
import { ReactComponent as EmailCircleIcon } from '../../../resources/icons/EmailCircle.svg';
import { ReactComponent as EmailFilledIcon } from '../../../resources/icons/EmailFilled.svg';
import { ReactComponent as EmailOutlineIcon } from '../../../resources/icons/EmailOutline.svg';
import { ReactComponent as EmailTemplateIcon } from '../../../resources/icons/EmailTemplate.svg';
import { ReactComponent as ErrorIcon } from '../../../resources/icons/Error.svg';
import { ReactComponent as ExpandIcon } from '../../../resources/icons/Expand.svg';
import { ReactComponent as ExportIcon } from '../../../resources/icons/Export.svg';
import { ReactComponent as HidePasswordIcon } from '../../../resources/icons/HidePassword.svg';
import { ReactComponent as ImageIcon } from '../../../resources/icons/Image.svg';
import { ReactComponent as ImportIcon } from '../../../resources/icons/Import.svg';
import { ReactComponent as LinkIcon } from '../../../resources/icons/Link.svg';
import { ReactComponent as LoadingIcon } from '../../../resources/icons/Loading.svg';
import { ReactComponent as LongTextIcon } from '../../../resources/icons/LongText.svg';
import { ReactComponent as MenuIcon } from '../../../resources/icons/Menu.svg';
import { ReactComponent as NegativeFilledIcon } from '../../../resources/icons/NegativeFilled.svg';
import { ReactComponent as NegativeOutlineIcon } from '../../../resources/icons/NegativeOutline.svg';
import { ReactComponent as NumberIcon } from '../../../resources/icons/Number.svg';
import { ReactComponent as PayFilledIcon } from '../../../resources/icons/PayFilled.svg';
import { ReactComponent as PayOutlineIcon } from '../../../resources/icons/PayOutline.svg';
import { ReactComponent as PlusIcon } from '../../../resources/icons/Plus.svg';
import { ReactComponent as PreviewIcon } from '../../../resources/icons/Preview.svg';
import { ReactComponent as ReplaceIcon } from '../../../resources/icons/Replace.svg';
import { ReactComponent as ResetIcon } from '../../../resources/icons/Reset.svg';
import { ReactComponent as SearchIcon } from '../../../resources/icons/Search.svg';
import { ReactComponent as SearchEmptyStateIcon } from '../../../resources/icons/SearchEmptyState.svg';
import { ReactComponent as SelectIcon } from '../../../resources/icons/Select.svg';
import { ReactComponent as SelectedFilledIcon } from '../../../resources/icons/SelectedFilled.svg';
import { ReactComponent as SelectedOutlineIcon } from '../../../resources/icons/SelectedOutline.svg';
import { ReactComponent as SettingsIcon } from '../../../resources/icons/Settings.svg';
import { ReactComponent as SettingsErrorIcon } from '../../../resources/icons/SettingsError.svg';
import { ReactComponent as ShowPasswordIcon } from '../../../resources/icons/ShowPassword.svg';
import { ReactComponent as SignOutIcon } from '../../../resources/icons/SignOut.svg';
import { ReactComponent as SortIcon } from '../../../resources/icons/Sort.svg';
import { ReactComponent as SplitViewIcon } from '../../../resources/icons/SplitView.svg';
import { ReactComponent as TagIcon } from '../../../resources/icons/Tag.svg';
import { ReactComponent as TeamFilledIcon } from '../../../resources/icons/TeamFilled.svg';
import { ReactComponent as TeamHollowIcon } from '../../../resources/icons/TeamHollow.svg';
import { ReactComponent as TextIcon } from '../../../resources/icons/Text.svg';
import { ReactComponent as TimeIcon } from '../../../resources/icons/Time.svg';
import { ReactComponent as UploadCircleIcon } from '../../../resources/icons/UploadCircle.svg';
import { ReactComponent as UserFilledIcon } from '../../../resources/icons/UserFilled.svg';
import { ReactComponent as UserOutlineIcon } from '../../../resources/icons/UserOutline.svg';
import { ReactComponent as UsersFilledIcon } from '../../../resources/icons/UsersFilled.svg';
import { ReactComponent as UsersOutlineIcon } from '../../../resources/icons/UsersOutline.svg';
import { ReactComponent as FilterList } from '../../../resources/icons/FilterList.svg';
import { ReactComponent as FilterFilled } from '../../../resources/icons/FilterFilled.svg';

const Icon: React.FC<IconCombinedProps> = (props) => {
  const {
    asset,
    colour,
    className,
  } = usePresenter(props);

  const variantName = `${asset}${colour}`;
  const currentStyle = styles[`icon${variantName}`];

  let componentView;

  switch (variantName) {
    case 'AddIconHover':
    case 'AddIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <AddIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'AddFilledIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <AddFilledIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'AddOutlineIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <AddOutlineIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'ArrowDownIconDisabled':
    case 'ArrowDownIconPrimary':
    case 'ArrowDownIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <ArrowDownIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'ArrowUpIconDisabled':
    case 'ArrowUpIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <ArrowUpIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'AttachmentIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <AttachmentIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'BackIconLight':
    case 'BackIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <BackIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'CalendarIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <CalendarIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'CalendarFilledIconDark':
    case 'CalendarFilledIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <CalendarFilledIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'CardMenuIconLight':
    case 'CardMenuIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <CardMenuIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'CheckBoxFilledIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <CheckBoxFilledIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'CheckboxIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <CheckboxIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'ChevronDownIconDark':
    case 'ChevronDownIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <ChevronDownIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'ChevronLeftIconDark':
    case 'ChevronLeftIconPrimary':
    case 'ChevronLeftIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <ChevronLeftIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'ChevronRightIconDark':
    case 'ChevronRightIconPrimary':
    case 'ChevronRightIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <ChevronRightIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'ChevronUpIconDark':
    case 'ChevronUpIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <ChevronUpIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'ClockIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <ClockIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'CloseIconDark':
    case 'CloseIconDestructive':
    case 'CloseIconLight':
    case 'CloseIconPositiveHover':
    case 'CloseIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <CloseIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'CodeIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <CodeIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'CopyIconLight':
    case 'CopyIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <CopyIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'DataFilledIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <DataFilledIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'DataOutlineIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <DataOutlineIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'DatePickerFilledIconDark':
    case 'DatePickerFilledIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <DatePickerFilledIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'DatePickerOutlineIconDark':
    case 'DatePickerOutlineIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <DatePickerOutlineIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'DeleteIconDark':
    case 'DeleteIconDestructive': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <DeleteIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'DocumentFillIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <DocumentFillIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'DropDownFilledIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <DropDownFilledIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'DropDownOutlineIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <DropDownOutlineIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'EmailFilledIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <EmailFilledIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'EmailOutlineIconPrimary':
    case 'EmailOutlineIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <EmailOutlineIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'ErrorIconDestructive': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <ErrorIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'ExpandIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <ExpandIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'HidePasswordIconPrimary':
    case 'HidePasswordIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <HidePasswordIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'ImageIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <ImageIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'ImportIconLight': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <ImportIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'LinkIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <LinkIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'LoadingIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <LoadingIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'LongTextIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <LongTextIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'MenuIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <MenuIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'NumberIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <NumberIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'PayOutlineIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <PayOutlineIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'PlusIconDark':
    case 'PlusIconDisabled':
    case 'PlusIconFocused':
    case 'PlusIconHover':
    case 'PlusIconLight':
    case 'PlusIconPrimary':
    case 'PlusIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <PlusIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'PreviewIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <PreviewIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'ReplaceIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <ReplaceIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'SearchIconInteractive':
    case 'SearchIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <SearchIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'SelectIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <SelectIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'ShowPasswordIconPrimary':
    case 'ShowPasswordIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <ShowPasswordIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'SignOutIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <SignOutIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'SortIconDisabled':
    case 'SortIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <SortIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'SplitViewIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <SplitViewIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'TagIconDark':
    case 'TagIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <TagIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'TeamFilledIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <TeamFilledIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'TeamHollowIconDark':
    case 'TeamHollowIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <TeamHollowIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'TextIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <TextIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'AddRoundedFillIconHover':
    case 'AddRoundedFillIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <AddRoundedFillIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'ArrowRightIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <ArrowRightIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'CheckIconPositiveHover': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <CheckIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'DeleteUserIconDestructive': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <DeleteUserIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'DocumentIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <DocumentIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'EmailFilledIconPositiveHover': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <EmailFilledIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'ExportIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <ExportIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'NegativeFilledIconDestructive': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <NegativeFilledIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'NegativeOutlineIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <NegativeOutlineIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'PayFilledIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <PayFilledIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'ResetIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <ResetIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'SearchEmptyStateIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <SearchEmptyStateIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'SelectedFilledIconPositiveHover':
    case 'SelectedFilledIconPrimary':
    case 'SelectedFilledIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <SelectedFilledIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'SelectedOutlineIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <SelectedOutlineIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'TimeIconWarning': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <TimeIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'UserFilledIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <UserFilledIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'UserOutlineIconDark':
    case 'UserOutlineIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <UserOutlineIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'UsersFilledIconPrimary':
    case 'UsersFilledIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <UsersFilledIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'CodeViewIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <CodeViewIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'DocumentCircleIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <DocumentCircleIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'EmailIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <Email style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'EmailCircleIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <EmailCircleIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'EmailTemplateIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <EmailTemplateIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'SettingsIconDark':
    case 'SettingsIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <SettingsIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'SettingsErrorIconDark':
    case 'SettingsErrorIconDestructive': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <SettingsErrorIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'UploadCircleIconPrimary': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <UploadCircleIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'UsersOutlineIconSubdued': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <UsersOutlineIcon style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'FilterListIconDisabled':
    case 'FilterListIconPrimary':
    case 'FilterListIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <FilterList style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    case 'FilterFilledIconDisabled':
    case 'FilterFilledIconPrimary':
    case 'FilterFilledIconDark': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <FilterFilled style={{ width: '100%', height: '100%' }}/>
        </div>
      );
      break;
    }
    default:
      componentView = <div>�</div>;
      break;
  }

  return componentView;
};

export default Icon;
